import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE, fmtDate, mediaUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { MapPin, X, Camera, Trash2, Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";

/**
 * Generic media gallery linked to any entity (daily_log, place, asset, ...).
 * Props:
 *  - linkedType: string (e.g. "place")
 *  - linkedId: string
 *  - allowUpload: bool
 *  - captionSlot: optional react node to display above gallery (e.g. count)
 */
export default function MediaGallery({ linkedType, linkedId, allowUpload = false, compact = false }) {
  const t = useT();
  const { user } = useAuth();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [open, setOpen] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const reload = async () => {
    if (!linkedId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/media", { params: { linked_type: linkedType, linked_id: linkedId } });
      setMedia(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [linkedId]);

  const onPick = () => fileRef.current?.click();
  const onPickCamera = () => cameraRef.current?.click();

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("linked_type", linkedType);
        fd.append("linked_id", linkedId);
        // best-effort EXIF lat/lng handled server-side for images
        await api.post("/media/upload", fd);
        ok++;
      } catch {
        fail++;
      }
    }
    setUploading(false);
    e.target.value = "";
    if (ok) toast.success(t("gallery.uploaded", { count: ok }));
    if (fail) toast.error(t("gallery.uploadFailed", { count: fail }));
    await reload();
  };

  const canDelete = (m) =>
    ["super_admin", "owner_director", "operations_admin"].includes(user?.role) ||
    m.uploaded_by_user_id === user?.id;

  const onDelete = async (m) => {
    setDeleting(m.id);
    try {
      await api.delete(`/media/${m.id}`);
      toast.success(t("gallery.deleted"));
      setOpen(null);
      await reload();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Failed");
    } finally {
      setDeleting(null);
    }
  };

  const empty = !loading && media.length === 0;

  return (
    <div className={compact ? "" : "mt-3"} data-testid={`gallery-${linkedType}-${linkedId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] flex items-center gap-1">
          <Camera className="w-3 h-3" /> {t("gallery.photos")} ({media.length})
        </div>
        {allowUpload && (
          <div className="flex items-center gap-3">
            {/* iOS Safari-safe hidden inputs: visually hidden but still focusable/triggerable */}
            <input
              ref={fileRef} type="file" accept="image/*,.heic,.heif" multiple
              style={{ position: "absolute", left: "-9999px", opacity: 0, width: 1, height: 1 }}
              onChange={onUpload} data-testid={`gallery-upload-input-${linkedId}`}
            />
            <input
              ref={cameraRef} type="file" accept="image/*" capture="environment"
              style={{ position: "absolute", left: "-9999px", opacity: 0, width: 1, height: 1 }}
              onChange={onUpload} data-testid={`gallery-camera-input-${linkedId}`}
            />
            <button
              type="button" onClick={onPickCamera} disabled={uploading}
              data-testid={`gallery-camera-btn-${linkedId}`}
              title={t("gallery.takePhoto")}
              className="md:hidden flex items-center gap-1 text-[11px] font-mono tracking-widest text-[#4DA6FF] hover:text-[#F8FAFC] disabled:opacity-50"
            >
              <Camera className="w-3 h-3" />
              {t("gallery.takePhoto")}
            </button>
            <button
              type="button" onClick={onPick} disabled={uploading}
              data-testid={`gallery-upload-btn-${linkedId}`}
              className="flex items-center gap-1 text-[11px] font-mono tracking-widest text-[#4DA6FF] hover:text-[#F8FAFC] disabled:opacity-50"
            >
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImagePlus className="w-3 h-3" />}
              {uploading ? t("gallery.uploading") : t("gallery.add")}
            </button>
          </div>
        )}
      </div>

      {empty ? (
        <div className="text-[11px] text-[#64748B] font-mono italic">{t("gallery.empty")}</div>
      ) : (
        <div className="flex gap-2 flex-wrap">
          {media.map(m => (
            <button
              key={m.id} type="button" onClick={() => setOpen(m)}
              data-testid={`gallery-thumb-${m.id}`}
              className="relative w-20 h-20 rounded-md overflow-hidden border border-[#1E293B] hover:border-[#4DA6FF] transition-colors bg-[#050A14]"
            >
              <img src={mediaUrl(m.id)} alt="" className="w-full h-full object-cover" />
              {m.latitude != null && (
                <div className="absolute bottom-0 left-0 right-0 bg-[#050A14]/80 px-1 py-0.5 text-[9px] font-mono text-[#4DA6FF] flex items-center gap-0.5">
                  <MapPin className="w-2.5 h-2.5" /> geo
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80"
          onClick={() => setOpen(null)} data-testid="gallery-lightbox"
        >
          <div className="ledger-card max-w-2xl w-full relative" onClick={(e) => e.stopPropagation()}>
            <div className="absolute top-3 right-3 z-10 flex gap-2">
              {canDelete(open) && (
                <button
                  onClick={() => onDelete(open)} disabled={deleting === open.id}
                  data-testid={`gallery-delete-${open.id}`}
                  className="p-2 rounded-md bg-[#050A14]/80 hover:bg-[#7F1D1D] text-[#F87171] hover:text-white transition-colors"
                >
                  {deleting === open.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              )}
              <button
                onClick={() => setOpen(null)}
                className="p-2 rounded-md bg-[#050A14]/80 hover:bg-[#131C31]"
              ><X className="w-4 h-4" /></button>
            </div>
            <img src={mediaUrl(open.id)} alt="" className="w-full max-h-[70vh] object-contain bg-[#050A14]" />
            <div className="p-4 text-xs font-mono text-[#94A3B8] flex items-center gap-2 flex-wrap">
              {open.latitude != null && (<><MapPin className="w-3 h-3" /> {open.latitude.toFixed(5)}, {open.longitude.toFixed(5)}</>)}
              {open.captured_at && <span>· {t("gallery.captured")} {fmtDate(open.captured_at)}</span>}
              {open.caption && <span className="text-[#F8FAFC]">· {open.caption}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
